import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card, Space, Typography, Tag, Divider, Alert, Spin } from 'antd';
import { SyncOutlined, CheckCircleOutlined, CloudDownloadOutlined } from '@ant-design/icons';
import { api } from '../../services/api';
import Button from '../../components/common/Button';

const { Title, Text } = Typography;

interface UpdateStatus {
  local_commit: string;
  local_message: string;
  remote_commit: string;
  remote_message: string;
  behind_by: number;
  ahead_by: number;
  up_to_date: boolean;
  fetch_error?: string;
}

export default function CorSystemUpdate() {
  const [status, setStatus] = useState<UpdateStatus | null>(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [triggering, setTriggering] = useState(false);
  const [log, setLog] = useState('');
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState('');
  const logRef = useRef<HTMLPreElement>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const prevLogRef = useRef('');

  const fetchStatus = useCallback(async () => {
    setStatusLoading(true);
    setError('');
    try {
      const res = await api.get('/system/update-status');
      setStatus(res.data);
    } catch (e: any) {
      const status = e?.response?.status;
      const msg = e?.response?.data?.error || e?.response?.data?.message || e?.message || 'Bilinmeyen hata';
      setError(`Hata ${status ? `(HTTP ${status})` : '(network)'}: ${msg}`);
    } finally {
      setStatusLoading(false);
    }
  }, []);

  const fetchLog = useCallback(async () => {
    try {
      const res = await api.get('/system/update-log?lines=80');
      const newLog: string = res.data.log || '';
      setLog(newLog);

      // Guncelleme bitti mi? Son satirda "successful" veya "ERROR" var mi?
      if (updating) {
        const lastLines = newLog.split('\n').slice(-5).join('\n');
        if (
          lastLines !== prevLogRef.current &&
          (lastLines.includes('successful') || lastLines.includes('ERROR') || lastLines.includes('SKIPPING'))
        ) {
          setUpdating(false);
          setTriggering(false);
          fetchStatus();
        }
      }
      prevLogRef.current = newLog.split('\n').slice(-5).join('\n');
    } catch {
      // Log alinamazsa sessizce gec
    }
  }, [updating, fetchStatus]);

  useEffect(() => {
    fetchStatus();
    fetchLog();
  }, []);

  // Log'u izle: guncelleme sirasinda 3sn arayla, normal modda yok
  useEffect(() => {
    if (updating) {
      pollRef.current = setInterval(fetchLog, 3000);
    } else {
      if (pollRef.current) clearInterval(pollRef.current);
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [updating, fetchLog]);

  // Log degisince en alta kaydir
  useEffect(() => {
    if (logRef.current) {
      logRef.current.scrollTop = logRef.current.scrollHeight;
    }
  }, [log]);

  const handleTrigger = async () => {
    setTriggering(true);
    setUpdating(true);
    setError('');
    try {
      await api.post('/system/trigger-update');
      // Biraz bekle, sonra log'u hemen goster
      setTimeout(fetchLog, 2000);
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Guncelleme tetiklenemedi.');
      setTriggering(false);
      setUpdating(false);
    }
  };

  return (
    <Card style={{ maxWidth: 800, margin: '0 auto' }}>
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <Title level={4} style={{ marginBottom: 0 }}>
          <CloudDownloadOutlined style={{ marginRight: 8 }} />
          Sistem Guncellemesi
        </Title>
        <Text type="secondary">
          GitHub'dan son commit'i cek ve projeyi yeniden derle.
        </Text>

        {error && <Alert type="error" message={error} showIcon />}
        {status?.fetch_error && (
          <Alert
            type="warning"
            showIcon
            message="GitHub'a ulasilamadi, gosterilen GitHub bilgisi eski olabilir."
            description={status.fetch_error}
          />
        )}

        {/* Versiyon Bilgisi */}
        <Card size="small" style={{ background: '#fafafa' }}>
          {statusLoading ? (
            <Spin size="small" />
          ) : status ? (
            <Space direction="vertical" size={4} style={{ width: '100%' }}>
              <Space>
                <Text type="secondary">Sunucu:</Text>
                <Tag color="blue">{status.local_commit}</Tag>
                <Text>{status.local_message}</Text>
              </Space>
              <Space>
                <Text type="secondary">GitHub:</Text>
                <Tag color="purple">{status.remote_commit}</Tag>
                <Text>{status.remote_message}</Text>
              </Space>
              {status.up_to_date ? (
                <Tag icon={<CheckCircleOutlined />} color="success">Guncel</Tag>
              ) : (
                <Tag color="warning">{status.behind_by} yeni commit var</Tag>
              )}
            </Space>
          ) : (
            <Text type="secondary">—</Text>
          )}
        </Card>

        {/* Butonlar */}
        <Space>
          <Button
            icon={<SyncOutlined spin={statusLoading} />}
            onClick={fetchStatus}
            loading={statusLoading}
            disabled={updating}
          >
            Durumu Yenile
          </Button>
          <Button action="edit"
            type="primary"
            icon={<CloudDownloadOutlined />}
            onClick={handleTrigger}
            loading={triggering}
            disabled={statusLoading}
          >
            {triggering ? 'Guncelleniyor...' : 'Simdi Guncelle'}
          </Button>
        </Space>

        {updating && (
          <Alert
            type="info"
            showIcon
            message="Guncelleme calistirildi. Log asagida canli olarak guncelleniyor..."
          />
        )}

        {/* Log Goruntuleyici */}
        <Divider style={{ margin: '8px 0' }}>Guncelleme Logu</Divider>
        <div style={{ position: 'relative' }}>
          {updating && (
            <SyncOutlined
              spin
              style={{ position: 'absolute', top: 8, right: 8, color: '#1890ff' }}
            />
          )}
          <pre
            ref={logRef}
            style={{
              background: '#1e1e1e',
              color: '#d4d4d4',
              padding: 12,
              borderRadius: 6,
              fontSize: 12,
              lineHeight: 1.6,
              maxHeight: 400,
              overflowY: 'auto',
              margin: 0,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-all',
            }}
          >
            {log || 'Hen\u00fcz log yok.'}
          </pre>
        </div>

        <Button size="small" onClick={fetchLog}>Logu Yenile</Button>
      </Space>
    </Card>
  );
}
